import { useState, useEffect } from "react";
import { Instagram } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import SEO from "@/components/SEO";
import { generateBreadcrumbSchema } from "@/utils/structuredData";
import AutoScrollGallery from "@/components/AutoScrollGallery";
import ImageLightbox from "@/components/ImageLightbox";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const Gallery = () => {
  const [communityImages, setCommunityImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Gallery', url: '/gallery' }
  ]);

  useEffect(() => {
    fetchCommunityImages();
  }, []);

  const fetchCommunityImages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/reviews/recent-gallery-images`);
      if (response.ok) {
        const data = await response.json();
        setCommunityImages(data.images || []);
      }
    } catch (error) {
      console.error("Error fetching community images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900">
      <SEO 
        title="Photo Gallery - George Dives | Underwater Photography & Dive Adventures"
        description="Explore stunning underwater photography and diving moments from around the world. Follow George's diving adventures, PADI course highlights, and marine life encounters on Instagram @george_dives."
        keywords={['underwater photography', 'dive photos', 'scuba diving gallery', 'diving adventures', 'marine life', 'dive trip photos']}
        type="website"
        structuredData={breadcrumbSchema}
      />
      
      <Navigation />

      <HeroSection 
        title="Underwater Gallery,"
        subtitle="Capturing the Magic"
        description="Explore stunning underwater photography from dive sites around the world."
        backgroundImage="https://images.unsplash.com/photo-1682687220795-796d3f6f7000?crop=entropy&cs=srgb&fm=jpg&q=85"
      />

      {/* Gallery Content */}
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Community Gallery - Auto-scrolling section */}
        {!loading && communityImages.length > 0 && (
          <AutoScrollGallery 
            images={communityImages}
            onImageClick={handleImageClick}
          />
        )}
        
        {/* Placeholder when no community images */}
        {!loading && communityImages.length === 0 && (
          <div className="mb-12 bg-white/5 backdrop-blur-md border-white/10 border-2 rounded-lg p-8 text-center">
            <div className="max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Instagram className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Community Gallery Coming Soon!</h3>
              <p className="text-cyan-300 mb-4">
                Be the first to contribute! Registered members can upload photos from their dive adventures.
              </p>
              <p className="text-gray-400 text-sm">
                In the meantime, check out the Instagram feed below for the latest underwater photos.
              </p>
            </div>
          </div>
        )}

        <Card className="bg-white/5 backdrop-blur-md border-white/10 text-white">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                <Instagram className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-4xl mb-4">Instagram Gallery</CardTitle>
            <CardDescription className="text-cyan-300 text-lg">
              Follow my underwater adventures on Instagram
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="bg-white/10 rounded-lg p-4 md:p-6">
              <p className="text-gray-300 mb-6 text-center">
                Check out my latest diving photos, course highlights, and underwater discoveries!
              </p>
              
              {/* Instagram Feed Embed - Fully Responsive */}
              <div className="bg-white rounded-lg p-2 md:p-4 overflow-hidden">
                <div className="relative w-full" style={{ paddingBottom: '100%', maxWidth: '765px', margin: '0 auto' }}>
                  <iframe 
                    src="https://snapwidget.com/embed/1113744" 
                    className="absolute top-0 left-0 w-full h-full" 
                    allowTransparency="true" 
                    frameBorder="0" 
                    scrolling="yes" 
                    title="Instagram Posts from George Dives"
                    loading="lazy"
                  ></iframe>
                </div>
              </div>
              
              <div className="mt-6 text-center">
                <a 
                  href="https://www.instagram.com/george_dives/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-full font-semibold transition-all shadow-lg"
                >
                  <Instagram className="w-5 h-5" />
                  Follow @george_dives
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <FloatingUserMenu />

      {/* Lightbox for community images */}
      <ImageLightbox
        images={communityImages.map(img => getImageUrl(img.image_url))}
        initialIndex={currentImageIndex}
        isOpen={lightboxOpen}
        onClose={handleCloseLightbox}
      />
      <Footer />

    </div>
  );
};

export default Gallery;